import { apiClient } from './client';

export interface PaymentResult {
    success: boolean;
    paymentId?: string;
    error?: string;
    data?: any;
}

export const loadRazorpay = () => {
    return new Promise((resolve) => {
        if ((window as any).Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

/**
 * Initialize payment using backend-first approach
 */
export const initializePayment = async (options: {
    amount: number;
    description: string;
    metadata?: Record<string, any>;
    userId: string;
    orderId: string;
    userName?: string;
    userEmail?: string;
    userPhone?: string;
}) => {
    // 1. Get payment order/intent from backend
    const { data: response } = await apiClient.post('/payments/initiate', {
        amount: Math.round(options.amount * 100), // paise
        userId: options.userId,
        orderId: options.orderId,
        currency: 'INR',
        metadata: options.metadata
    });

    if (!response.success) {
        throw new Error(response.error || "Failed to initiate payment with backend");
    }

    const { paymentId, gateway, checkoutUrl, gatewayOrderId } = response.data;

    // 2. Handle specific gateway flow
    if (gateway === 'RAZORPAY') {
        const sdkLoaded = await loadRazorpay();
        if (!sdkLoaded) throw new Error("Razorpay SDK failed to load.");

        return new Promise((resolve, reject) => {
            const rzp = new (window as any).Razorpay({
                key: response.data.keyId || process.env.REACT_APP_RAZORPAY_KEY,
                amount: Math.round(options.amount * 100),
                currency: "INR",
                name: "Gaadiwala",
                description: options.description,
                order_id: gatewayOrderId,
                handler: function (res: any) {
                    resolve({
                        success: true,
                        paymentId: res.razorpay_payment_id,
                        data: { ...res, internalPaymentId: paymentId }
                    });
                },
                prefill: {
                    name: options.userName,
                    email: options.userEmail,
                    contact: options.userPhone,
                },
                theme: { color: "#00D1FF" },
                modal: { ondismiss: () => reject(new Error("Payment cancelled")) }
            });
            rzp.open();
        });
    }

    // 3. Handle redirect flow (if applicable for other gateways)
    if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return { success: true, redirect: true };
    }

    throw new Error(`Unsupported gateway flow: ${gateway}`);
};

/**
 * Verify payment status with backend
 */
export const verifyPayment = async (paymentId: string) => {
    const { data } = await apiClient.get(`/payments/${paymentId}/status`);
    return data;
};

/**
 * Wallet Top-up (Backend Driven)
 */
export const topUpWallet = async (userId: string, amount: number, paymentId: string) => {
    const { data } = await apiClient.post('/wallet/topup', { userId, amount, paymentId });
    return data.success;
};

/**
 * Wallet Deduction (Backend Driven)
 */
export const deductFromWallet = async (userId: string, amount: number, description: string, rideId?: string) => {
    const { data } = await apiClient.post('/wallet/deduct', { userId, amount, description, rideId });
    return data.success;
};
